import React, { useEffect, useState } from 'react';
import apiService from '../../services/ApiService';
import {
  PASSPORT_STICKER_FORMATS,
  loadPassportBrandLockup,
  loadSvgImage,
  normalizePassportStickerSubjectLabel,
  renderPassportSticker,
  type PassportStickerFormat,
} from '../../utils/passportStickerCanvas';

/** Only two sizes. Same portrait design on both. */
const FORMAT_ORDER: PassportStickerFormat[] = ['portrait_3in', 'portrait_2in'];

type Props = {
  qrSvgInverted?: string | null;
  qrSvg?: string | null;
  scanUrl: string | null;
  /** Footer label on the vinyl (shop, club, build name). */
  subjectLabel?: string | null;
  title?: string;
  /** Extra line under the sticker intro (e.g. one-code note for vehicles). */
  support?: string | null;
};

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    return false;
  }
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const PassportStickerStudio: React.FC<Props> = ({
  qrSvgInverted,
  qrSvg,
  scanUrl,
  subjectLabel,
  title = 'Passport sticker',
  support = null,
}) => {
  const footerLabel = normalizePassportStickerSubjectLabel(subjectLabel);
  const [format, setFormat] = useState<PassportStickerFormat>('portrait_3in');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const svg = qrSvgInverted || qrSvg || '';

  useEffect(() => {
    if (!svg) {
      setPreviewUrl(null);
      return undefined;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setBusy(true);
    setError(null);
    (async () => {
      try {
        const [qrImage, brandLockup] = await Promise.all([
          loadSvgImage(svg),
          loadPassportBrandLockup(),
        ]);
        const blob = await renderPassportSticker({
          format,
          qrImage,
          brandLockup,
          subjectLabel: footerLabel,
        });
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewUrl(objectUrl);
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not render sticker');
          setPreviewUrl(null);
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [svg, format, footerLabel]);

  const spec = PASSPORT_STICKER_FORMATS[format];

  const handleDownload = async () => {
    if (!svg) return;
    try {
      const [qrImage, brandLockup] = await Promise.all([
        loadSvgImage(svg),
        loadPassportBrandLockup(),
      ]);
      const blob = await renderPassportSticker({ format, qrImage, brandLockup, subjectLabel: footerLabel });
      downloadBlob(blob, `bilt-passport-${format}.png`);
      void apiService.trackEvent('passport_sticker_downloaded', {
        metadata: { format, physical_size: spec.physicalSize },
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not download sticker');
    }
  };

  const handlePrint = () => {
    if (!previewUrl) return;
    const win = window.open('', '_blank', 'noopener,noreferrer,width=560,height=760');
    if (!win) return;
    const safeTitle = title.replace(/</g, '&lt;');
    win.document.write(`<!DOCTYPE html><html><head><title>${safeTitle}</title>
<style>
  @page { margin: 0.4in; }
  body { margin: 0; background: #111; color: #f5f5f5; font-family: system-ui, sans-serif; text-align: center; padding: 24px; }
  img { max-width: 100%; height: auto; }
  p { font-size: 12px; color: #a3a3a3; }
  @media print { body { background: #fff; color: #111; } }
</style></head><body>
  <img src="${previewUrl}" alt="${safeTitle}" />
  <p>${spec.physicalSize} · matte outdoor vinyl · ${safeTitle}</p>
  <script>window.onload=function(){window.print();}</script>
</body></html>`);
    win.document.close();
    void apiService.trackEvent('passport_sticker_printed', { metadata: { format } });
  };

  if (!svg && !scanUrl) {
    return null;
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xs uppercase tracking-wide text-meta">Sticker</h3>
        <p className="text-muted text-sm mt-1">
          {support ||
            'Black vinyl, white QR, Bilt:Spec lockup under the code at QR width. 3 in or 2 in. Print at 300 DPI.'}
        </p>
      </div>

      <fieldset className="space-y-2">
        <legend className="sr-only">Sticker size</legend>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Sticker size">
          {FORMAT_ORDER.map((id) => {
            const item = PASSPORT_STICKER_FORMATS[id];
            const selected = format === id;
            return (
              <button
                key={id}
                type="button"
                aria-pressed={selected}
                className={`btn-sm ${selected ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setFormat(id)}
              >
                {item.label}
                {item.recommended ? ' · recommended' : ''}
              </button>
            );
          })}
        </div>
        <p className="text-meta text-xs">
          {spec.physicalSize} portrait. {spec.useCase}.
        </p>
      </fieldset>

      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div className="rounded-2xl bg-black p-4 border border-white/10">
          {busy && !previewUrl ? (
            <div className="h-56 w-40 rounded-xl bg-white/5 animate-pulse" aria-busy="true" />
          ) : previewUrl ? (
            <img
              src={previewUrl}
              alt={`${spec.label} Passport sticker preview`}
              className="max-h-72 w-auto"
            />
          ) : (
            <p className="text-muted text-sm">Preview unavailable.</p>
          )}
        </div>
        <div className="flex flex-col gap-2 min-w-0">
          <button type="button" className="btn-primary" onClick={() => void handleDownload()} disabled={!svg}>
            Download PNG
          </button>
          <button type="button" className="btn-secondary" onClick={handlePrint} disabled={!previewUrl}>
            Print
          </button>
          {scanUrl ? (
            <button
              type="button"
              className="btn-secondary"
              onClick={async () => {
                const ok = await copyText(scanUrl);
                setCopied(ok);
                if (ok) {
                  window.setTimeout(() => setCopied(false), 2000);
                }
              }}
            >
              <span aria-live="polite">{copied ? 'Link copied' : 'Copy scan link'}</span>
            </button>
          ) : null}
          {qrSvg ? (
            <button
              type="button"
              className="btn-ghost"
              onClick={() =>
                downloadBlob(new Blob([qrSvg], { type: 'image/svg+xml' }), 'bilt-passport-scan-safe.svg')
              }
            >
              Scan-safe SVG
            </button>
          ) : null}
        </div>
      </div>
      {error ? (
        <p className="alert-error text-sm" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
};

export default PassportStickerStudio;
